import { useState } from 'react';
import {
  Alert, Button, Checkbox, Dropdown, Icon, InputAdornment, Select, TextField, Tooltip, Typography,
} from '@gupshup-frontend/gupshup-ts-components';
import {
  BUTTON_TYPE, FLOW_ACTION, HEADER_TYPE, URL_TYPE, COPY_CODE_BUTTON_LABEL,
  MARKETING_OPT_OUT_BUTTON_TEXT, URL_TYPE_LINK_TRACK_LABEL, LINK_TRACKING_NOTE,
  REQUEST_CONTACT_INFO_BUTTON_TEXT, REQUEST_CONTACT_INFO_WARNING,
  createButton, field, isAuthentication, isCallPermissionRequest, isCarousel, isLinkTracked, isLto,
  isMarketing, hasRequestContactInfo, quickRepliesFirst,
} from '../state';
import {
  checkCopyCodeFormat, checkDeepLinkFormat, checkDynamicUrlBase, checkPhoneFormat, checkUrlFormat,
  checkVoiceCallDaysFormat,
} from '../validate';
import { friendlyError } from './errorMessages';

// Mirrors validate.js's own (unexported) BUTTON_TEXT_MAX_CHARS / TOTAL_BUTTONS_MAX /
// CTA_BUTTON_TYPE_MAX. Duplicated here only to drive maxLength/disabled hints before a submit
// would otherwise be the only feedback — validate.js stays the single source of truth for what
// actually blocks a submit; keep these in sync with it by hand.
const BUTTON_TEXT_MAX_CHARS = 25;
const TOTAL_BUTTONS_MAX = 10;
const CTA_TYPE_MAX = {
  [BUTTON_TYPE.URL]: 2,
  [BUTTON_TYPE.PHONE_NUMBER]: 1,
  [BUTTON_TYPE.COPY_CODE]: 1,
  [BUTTON_TYPE.FLOW]: 1,
  [BUTTON_TYPE.VOICE_CALL]: 1,
  // ORDER_DETAILS / REQUEST_CONTACT_INFO have no cap in our DTOs — left out on purpose.
};

// PHONE_NUMBER and VOICE_CALL each cap at 1 above, but they also share ONE combined cap of 1 —
// mirrors the console's own isCtaMenuItemDisabled (createTemplateHooks.ts:216-225): once either
// type has a button anywhere in the list, both become uncreatable, not just whichever one is
// already sitting at its own individual cap.
const PHONE_VOICE_CALL_GROUP = [BUTTON_TYPE.PHONE_NUMBER, BUTTON_TYPE.VOICE_CALL];

// Single source both menuItems (Add CTA) and buildTypeOptions (a row's own "Select type") read a
// type's capped/disabled state from, so the two can never disagree. `excludeType` — a row's own
// CURRENT type, only ever passed by buildTypeOptions — is never counted against itself or its
// combined-group partner, so a row can freely switch between Phone Number and Voice Call in place
// without first being removed; menuItems (no row of its own) passes no excludeType and reads the
// live counts as they are.
function isCtaTypeCapped(type, countByType, excludeType) {
  if (PHONE_VOICE_CALL_GROUP.includes(type)) {
    if (type === excludeType) return false;
    const ownContribution = PHONE_VOICE_CALL_GROUP.includes(excludeType) ? 1 : 0;
    const combinedCount = PHONE_VOICE_CALL_GROUP.reduce((sum, t) => sum + (countByType[t] || 0), 0);
    return (combinedCount - ownContribution) >= 1;
  }
  const cap = CTA_TYPE_MAX[type];
  return type !== excludeType && cap !== undefined && (countByType[type] || 0) >= cap;
}

// COPY_CODE's buttonValue rides the wire as `example` (payload.js's COPY_CODE builder), capped by
// OUR backend's own DTO (gs-intg-services copy-code-button.dto.ts,
// TEMPLATE_LIMITS.copyCodeExampleMaxChars) — a different repo with no build-time import path into
// this one (same situation as Header.js's own MEDIA_RULES) — copied by hand.
//
// The console's own equivalent constant is 20. We deliberately do NOT adopt that value here: our
// wire caps at 15 and would reject anything above it no matter what the UI allows typing.
const COPY_CODE_MAX_CHARS = 15;

// Console's DEEP_LINK_CHARACTER_LENGTH (gs-ui-templates CreateTemplate/config.ts:339); applies to
// both the deep link and its fallback URL.
const DEEP_LINK_MAX_CHARS = 2000;

// payload.js's VOICE_CALL builder sends ttl_minutes = activeFor(days) * 1440; the server's
// accepted range is 1440-43200 minutes, i.e. 1-30 days. State (and this UI) stays in days.
const VOICE_CALL_MIN_DAYS = 1;
const VOICE_CALL_MAX_DAYS = 30;

// Console's PHONE_MAX_LENGTH (gs-ui-templates CreateTemplate/config.ts) — caps the Phone Number
// CTA's own button value.
const PHONE_VALUE_MAX_CHARS = 20;

// Console's SCREEN_NAME_REGEX_CHARACTER_LENGTH (gs-ui-templates CreateTemplate/config.ts) — caps
// the Flow button's screen name.
const SCREEN_NAME_MAX_CHARS = 5000;

// Console's DAYS_MAX_LENGTH (gs-ui-templates CreateTemplate/config.ts) — digit-length cap on the
// Voice Call "active for" input, distinct from the VOICE_CALL_MIN_DAYS/MAX_DAYS value range above.
const ACTIVE_FOR_MAX_CHARS = 2;

// Live, per-keystroke buttonValue format check — same checker functions validate.js's own
// submit-time validateButtons calls, keyed by type so adding/removing a checked type is a
// one-line change here instead of a new branch at each call site below. FLOW's buttonValue
// (screen name) has no format checker in validate.js either — the missing entry mirrors that.
const checkerByType = {
  [BUTTON_TYPE.URL]: checkUrlFormat,
  [BUTTON_TYPE.PHONE_NUMBER]: checkPhoneFormat,
  [BUTTON_TYPE.COPY_CODE]: checkCopyCodeFormat,
};

// Console-hosted icons this file swaps in for two of its own library icons, for console parity —
// TEMPLATE_ICONS.WHATSAPP_DRAG_BUTTON / WHATSAPP_BUTTON_INFO in gs-ui-templates' constants/icons.ts.
const DRAG_HANDLE_ICON_URL = 'https://gs-upload.gupshup.io/templates/icons/ic_drag.svg';
const MAX_BUTTON_LIMITS_INFO_ICON_URL = 'https://gs-upload.gupshup.io/templates/icons/ic_info_disabled.svg';

const BUTTON_TYPE_LABELS = {
  [BUTTON_TYPE.QUICK_REPLY]: 'Quick Reply',
  [BUTTON_TYPE.URL]: 'URL',
  [BUTTON_TYPE.PHONE_NUMBER]: 'Phone Number',
  // Console's own type label for this Select option (en.json's copy_offer_code) — distinct from
  // COPY_CODE_BUTTON_LABEL (state.js), the button's own fixed, locked title. Same string today,
  // different constant on purpose: they answer different questions and must stay free to diverge.
  [BUTTON_TYPE.COPY_CODE]: 'Copy offer code',
  [BUTTON_TYPE.FLOW]: 'Flow',
  // Console's own type label (en.json's voice_call) — only the displayed name changes.
  [BUTTON_TYPE.VOICE_CALL]: 'Call to WhatsApp',
  [BUTTON_TYPE.ORDER_DETAILS]: 'Order Details',
  [BUTTON_TYPE.REQUEST_CONTACT_INFO]: 'Request Contact Info',
};

// Types "Add CTA" currently offers. QUICK_REPLY has its own dedicated add-affordance; OTP/PASSKEY
// belong to Authentication's own fixed button (AuthButtonsText.js, separate task) and never reach
// this list. COPY_CODE is offered for Marketing templates only — mirrors the console's own gate
// (gs-ui-templates ButtonsCategory.tsx's CTA menu filters `item.value===copyCode && !isMarketing()`).
// VOICE_CALL is offered to every account (product decision 2026-09-24: a feature gated behind an
// Enterprise account flag is offered to everyone, as long as an account without the flag gets a
// clear error at create time) — Enterprise rejects an account without the voice module with "This
// account is not allowed to use voice call button or call_permission_request. Contact support to
// enable the voice module." (ce-api ValidationUtils.java:390-406; allow.voice.for.all=false in
// prod/qa) before the Meta call, and this form already surfaces that error text (Alert + toast)
// same as any other create/update failure. FLOW and ORDER_DETAILS stay withheld from this menu
// regardless of category — FLOW needs a published Meta Flow ID and no API lists a WABA's flows to
// build a picker from; ORDER_DETAILS has no account gate at all, but the Message tab can't supply
// the order payload a real send would need — both stay fully supported everywhere else in this
// file (caps, checkers, labels, extra-field rendering) so a template that already carries one
// still renders and validates correctly on edit.
const BASE_CTA_TYPES = [
  BUTTON_TYPE.URL,
  BUTTON_TYPE.PHONE_NUMBER,
  BUTTON_TYPE.VOICE_CALL,
  BUTTON_TYPE.REQUEST_CONTACT_INFO,
];

// Category-aware: only a Marketing template's Add CTA menu / per-row type Select offers COPY_CODE.
function getCtaTypes(state) {
  if (!isMarketing(state)) return BASE_CTA_TYPES;
  return [
    BUTTON_TYPE.URL, BUTTON_TYPE.PHONE_NUMBER, BUTTON_TYPE.VOICE_CALL,
    BUTTON_TYPE.COPY_CODE, BUTTON_TYPE.REQUEST_CONTACT_INFO,
  ];
}

// Offered to every account — Enterprise fills url/example from the account's link-tracking
// domain and rejects the save with a "contact support" message when the account isn't set up
// (URLButton.java:166-199). gs-intg passes it through unchecked.
const URL_TYPE_OPTIONS = [
  { value: URL_TYPE.STATIC, label: 'Static' },
  { value: URL_TYPE.DYNAMIC, label: 'Dynamic' },
  { value: URL_TYPE.DYNAMIC_LINK_TRACK, label: URL_TYPE_LINK_TRACK_LABEL },
].map((item) => ({
  ...item,
  'data-testid': `bloomreach-templates-form-buttons-urltype-option-${item.value.toLowerCase()}`,
}));

const FLOW_ACTION_OPTIONS = [
  { value: FLOW_ACTION.NAVIGATE, label: 'Navigate' },
  { value: FLOW_ACTION.DATA_EXCHANGE, label: 'Data exchange' },
].map((item) => ({
  ...item,
  'data-testid': `bloomreach-templates-form-buttons-flowaction-option-${item.value}`,
}));

export function errorProps(errors, path) {
  return { error: Boolean(errors[path]), helperText: friendlyError(errors[path]) };
}

// Same shape as errorProps, but for a field that also carries its own live, per-keystroke format
// error (buttonValue/activeFor only — see checkerByType above). The live error takes precedence
// over the submit-time errors[path] entry; friendlyError still runs over whichever one wins since
// it's a safe no-op on an already-friendly sentence (no MESSAGES key match, no underscores to
// swap) and is what actually translates errors[path] when there's no live error yet.
export function liveErrorProps(errors, path, liveError) {
  const displayError = liveError || errors[path];
  return { error: Boolean(displayError), helperText: friendlyError(displayError) };
}

// Fields specific to one button TYPE, beyond the {text} every type gets (rendered by the caller).
// Field names below are read straight off payload.js's CTA_BUILDERS, not guessed:
//   - COPY_CODE's code and FLOW's screen name both live in state as `buttonValue` (payload.js
//     maps them to the wire's `example` and `navigate_screen` respectively).
//   - FLOW's `flowId`/`flowAction` map to `flow_id`/`flow_action`.
function renderExtraFields({
  btn, index, dispatch, errors, disabled, deepLinkOfferable,
}) {
  const path = `buttons.${index}`;
  const update = (value) => dispatch({ type: 'updateButton', buttonId: btn.buttonId, value });
  // Shared by every buttonValue field below (URL/PHONE_NUMBER/COPY_CODE/FLOW) — computes the live
  // error for btn's own type via checkerByType and writes it in the same merge as the value.
  // DYNAMIC url buttons get one extra check on top — same rule as validate.js's submit-time check.
  const updateButtonValue = (value) => {
    const checker = checkerByType[btn.type];
    let error = checker ? checker(value) : '';
    if (!error && btn.type === BUTTON_TYPE.URL && btn.urlType.value === URL_TYPE.DYNAMIC) {
      error = checkDynamicUrlBase(value);
    }
    update({ buttonValue: { value, error } });
  };

  if (btn.type === BUTTON_TYPE.URL) {
    const isDynamic = btn.urlType.value === URL_TYPE.DYNAMIC;
    const isLinkTrack = isLinkTracked(btn);
    return (
      <>
        <Select
          label="Select URL type"
          items={URL_TYPE_OPTIONS}
          value={btn.urlType.value}
          handleChange={(selected) => update({
            urlType: { value: Array.isArray(selected) ? selected[0] : selected, error: '' },
          })}
          disabled={disabled}
          width="100%"
          data-testid={`bloomreach-templates-form-buttons-urltype-${btn.buttonId}`}
        />
        {errors[`${path}.urlType.value`] && (
          <Typography variant="caption" color="error" data-testid={`bloomreach-templates-form-buttons-urltype-error-${btn.buttonId}`}>
            {friendlyError(errors[`${path}.urlType.value`])}
          </Typography>
        )}
        {isLinkTrack ? (
          // Enterprise ignores our url/example for this type and fills both from the account's
          // link-tracking domain, rejecting the save if the account isn't set up
          // (URLButton.java:166-199) — nothing to type here.
          <Typography variant="caption" data-testid={`bloomreach-templates-form-buttons-url-linktrack-note-${btn.buttonId}`}>
            {LINK_TRACKING_NOTE}
          </Typography>
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              value={btn.buttonValue.value}
              onChange={(event) => updateButtonValue(event.target.value)}
              disabled={disabled}
              placeholder="Button Value"
              autoComplete="off"
              inputProps={{ 'data-testid': `bloomreach-templates-form-buttons-url-${btn.buttonId}` }}
              {...liveErrorProps(errors, `${path}.buttonValue.value`, btn.buttonValue.error)}
            />
            {isDynamic && (
              <div className="br-tpl-form-url-suffix-row" data-testid={`bloomreach-templates-form-buttons-url-suffix-row-${btn.buttonId}`}>
                {/* bare {{1}} in JSX children parses as an expression container, not text — kept as
                    a JS string literal, same reason as the sibling comment this replaced. */}
                <span className="br-tpl-form-url-suffix-token">{'{{1}}'}</span>
                <TextField
                  fullWidth
                  size="small"
                  value={btn.suffix.value}
                  onChange={(event) => update({ suffix: { value: event.target.value, error: '' } })}
                  disabled={disabled}
                  placeholder="Type Sample Suffix"
                  autoComplete="off"
                  inputProps={{ 'data-testid': `bloomreach-templates-form-buttons-url-suffix-${btn.buttonId}` }}
                  {...errorProps(errors, `${path}.suffix.value`)}
                />
                <Tooltip
                  placement="top"
                  title={(
                    <>
                      Sample dynamic URL with variable
                      <br />
                      Note: Do not add the URL in your sample message body above
                    </>
                  )}
                  data-testid={`bloomreach-templates-form-buttons-url-suffix-tooltip-${btn.buttonId}`}
                >
                  <span>
                    <Icon name="question" size={18} showCursor />
                  </span>
                </Tooltip>
              </div>
            )}
          </>
        )}
        {/* Offered for a NEW url button only when the template is Marketing AND the channel's own
            WhatsApp number is MM Lite (ButtonsSection's deepLinkOfferable, built from the isMmLite
            prop threaded down via sections/index.js's extraProps) — Enterprise SILENTLY drops
            app_deep_link otherwise (ce-api MessageTemplateService.java:2429-2436, :2562-2563), no
            error surfaces for this form to show, so offering it outside that gate would let a user
            believe a field saved when it silently didn't. A button that already has hasDeepLink
            true (an existing template's button, via prefill.js) keeps showing the checkbox and,
            while ticked, both fields — regardless of the gate — so the user can still see, edit or
            untick it even if the category/channel no longer qualify. */}
        {(btn.hasDeepLink || deepLinkOfferable) && (
          <>
            <div className="br-tpl-form-inline-row">
              <Checkbox
                checked={btn.hasDeepLink}
                onChange={(event) => {
                  const checked = event.target.checked;
                  // Unticking must clear both values, not just hide them — payload.js only omits
                  // app_deep_link while hasDeepLink is false, so a stale deepLink/fallbackLink left
                  // in state would resurface the moment the box is ticked again.
                  update(checked
                    ? { hasDeepLink: true }
                    : { hasDeepLink: false, deepLink: field(), fallbackLink: field() });
                }}
                label="Enable deep link"
                disabled={disabled}
                data-testid={`bloomreach-templates-form-buttons-deeplink-toggle-${btn.buttonId}`}
              />
              <Tooltip
                placement="right"
                // Deliberate one-word fix over the console's own copy (en.json's
                // track_and_analyse_app_conversation_rates): "conversion rates", not "conversation
                // rates" — the console's text is a typo, not wording worth preserving.
                title="Track and analyse app conversion rates using the Marketing Messages Lite API"
                data-testid={`bloomreach-templates-form-buttons-deeplink-toggle-info-${btn.buttonId}`}
              >
                <span>
                  <Icon name="warningCircle" size={20} showCursor />
                </span>
              </Tooltip>
            </div>
            {btn.hasDeepLink && (
              <>
                <div className="br-tpl-form-url-suffix-row">
                  <TextField
                    fullWidth
                    size="small"
                    label="Android deep link"
                    value={btn.deepLink.value}
                    onChange={(event) => {
                      const { value } = event.target;
                      update({ deepLink: { value, error: checkDeepLinkFormat(value) } });
                    }}
                    disabled={disabled}
                    placeholder="Android deep link"
                    autoComplete="off"
                    inputProps={{
                      maxLength: DEEP_LINK_MAX_CHARS,
                      'data-testid': `bloomreach-templates-form-buttons-deeplink-${btn.buttonId}`,
                    }}
                    {...liveErrorProps(errors, `${path}.deepLink.value`, btn.deepLink.error)}
                  />
                  <Tooltip
                    placement="right"
                    title="Enter the Android deep link URL to direct users to specific content within your app."
                    data-testid={`bloomreach-templates-form-buttons-deeplink-info-${btn.buttonId}`}
                  >
                    <span>
                      <Icon name="warningCircle" size={28} showCursor />
                    </span>
                  </Tooltip>
                </div>
                <div className="br-tpl-form-url-suffix-row">
                  <TextField
                    fullWidth
                    size="small"
                    label="Android fallback URL - Optional"
                    value={btn.fallbackLink.value}
                    onChange={(event) => {
                      const { value } = event.target;
                      update({ fallbackLink: { value, error: checkUrlFormat(value) } });
                    }}
                    disabled={disabled}
                    placeholder="Android fallback URL - Optional"
                    autoComplete="off"
                    inputProps={{
                      maxLength: DEEP_LINK_MAX_CHARS,
                      'data-testid': `bloomreach-templates-form-buttons-fallbacklink-${btn.buttonId}`,
                    }}
                    {...liveErrorProps(errors, `${path}.fallbackLink.value`, btn.fallbackLink.error)}
                  />
                  <Tooltip
                    placement="right"
                    title="Enter the fallback URL that opens in a browser when the app is not installed on the user's device."
                    data-testid={`bloomreach-templates-form-buttons-fallbacklink-info-${btn.buttonId}`}
                  >
                    <span>
                      <Icon name="warningCircle" size={28} showCursor />
                    </span>
                  </Tooltip>
                </div>
              </>
            )}
          </>
        )}
      </>
    );
  }

  if (btn.type === BUTTON_TYPE.PHONE_NUMBER) {
    return (
      <TextField
        fullWidth
        size="small"
        label="Button Value"
        value={btn.buttonValue.value}
        onChange={(event) => updateButtonValue(event.target.value)}
        disabled={disabled}
        placeholder="Type button value"
        autoComplete="off"
        // DTO requires at least 5 characters — validate.js now enforces that floor at submit
        // (phone_number_min_length). The minLength attribute itself still never fires: submit
        // goes through an onClick handler, not native form validation, so this stays a hint for
        // assistive tech/spinners rather than what actually blocks a short value.
        inputProps={{
          minLength: 5,
          maxLength: PHONE_VALUE_MAX_CHARS,
          'data-testid': `bloomreach-templates-form-buttons-phone-${btn.buttonId}`,
        }}
        {...liveErrorProps(errors, `${path}.buttonValue.value`, btn.buttonValue.error)}
      />
    );
  }

  if (btn.type === BUTTON_TYPE.COPY_CODE) {
    return (
      <TextField
        fullWidth
        size="small"
        label="Button Value"
        value={btn.buttonValue.value}
        onChange={(event) => updateButtonValue(event.target.value)}
        disabled={disabled}
        placeholder="Type button value"
        autoComplete="off"
        inputProps={{
          maxLength: COPY_CODE_MAX_CHARS,
          'data-testid': `bloomreach-templates-form-buttons-copycode-${btn.buttonId}`,
        }}
        {...liveErrorProps(errors, `${path}.buttonValue.value`, btn.buttonValue.error)}
      />
    );
  }

  if (btn.type === BUTTON_TYPE.FLOW) {
    return (
      <>
        <TextField
          fullWidth
          size="small"
          type="number"
          value={btn.flowId.value}
          onChange={(event) => update({ flowId: { value: event.target.value, error: '' } })}
          disabled={disabled}
          placeholder="Enter flow ID"
          autoComplete="off"
          inputProps={{ 'data-testid': `bloomreach-templates-form-buttons-flowid-${btn.buttonId}` }}
          {...errorProps(errors, `${path}.flowId.value`)}
        />
        <Select
          label="Flow action"
          items={FLOW_ACTION_OPTIONS}
          value={btn.flowAction.value}
          handleChange={(selected) => update({
            flowAction: { value: Array.isArray(selected) ? selected[0] : selected, error: '' },
          })}
          disabled={disabled}
          width="100%"
          data-testid={`bloomreach-templates-form-buttons-flowaction-${btn.buttonId}`}
        />
        <div className="br-tpl-form-url-suffix-row">
          <TextField
            fullWidth
            size="small"
            label="Screen Name"
            value={btn.buttonValue.value}
            onChange={(event) => updateButtonValue(event.target.value)}
            disabled={disabled}
            placeholder="Screen Name"
            autoComplete="off"
            inputProps={{
              maxLength: SCREEN_NAME_MAX_CHARS,
              'data-testid': `bloomreach-templates-form-buttons-flowscreen-${btn.buttonId}`,
            }}
            {...liveErrorProps(errors, `${path}.buttonValue.value`, btn.buttonValue.error)}
          />
          <Tooltip
            placement="right"
            title="User must enter the screen name of the selected flow correctly. Incase of any discrepancy in the screen name, the flow may not work as intended."
            data-testid={`bloomreach-templates-form-buttons-flowscreen-info-${btn.buttonId}`}
          >
            <span>
              <Icon name="warningCircle" size={18} showCursor />
            </span>
          </Tooltip>
        </div>
      </>
    );
  }

  if (btn.type === BUTTON_TYPE.VOICE_CALL) {
    return (
      <TextField
        fullWidth
        size="small"
        type="number"
        label="Active for"
        value={btn.activeFor.value}
        onChange={(event) => {
          const { value } = event.target;
          update({ activeFor: { value, error: checkVoiceCallDaysFormat(value) } });
        }}
        disabled={disabled}
        placeholder="Active for"
        autoComplete="off"
        InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
        inputProps={{
          min: VOICE_CALL_MIN_DAYS,
          max: VOICE_CALL_MAX_DAYS,
          maxLength: ACTIVE_FOR_MAX_CHARS,
          'data-testid': `bloomreach-templates-form-buttons-activefor-${btn.buttonId}`,
        }}
        {...liveErrorProps(errors, `${path}.activeFor.value`, btn.activeFor.error)}
      />
    );
  }

  if (btn.type === BUTTON_TYPE.REQUEST_CONTACT_INFO) {
    // Console-verbatim warning shown under the (locked) title field — ButtonsCategory.tsx:735-743.
    return (
      <Alert
        type="warning"
        description={REQUEST_CONTACT_INFO_WARNING}
        data-testid={`bloomreach-templates-form-buttons-rci-warning-${btn.buttonId}`}
      />
    );
  }

  // QUICK_REPLY / ORDER_DETAILS carry no field beyond `text`.
  return null;
}

// Options for a CTA row's own "Select type" dropdown — the same ctaTypes list (and the same
// CTA_TYPE_MAX caps) "Add CTA" already offers/enforces, so changing a row's type can never leave
// more buttons of one type than adding fresh ever could. `currentType` is the row's own type
// before the change: an option is only disabled when picking it would push some OTHER type's
// count to its cap — the row's own current slot never counts against itself, so its own type is
// always selectable even if the row was prefilled from a template that already exceeded a cap.
function buildTypeOptions(currentType, countByType, ctaTypes) {
  const options = ctaTypes.map((type) => {
    const capped = isCtaTypeCapped(type, countByType, currentType);
    return {
      value: type,
      label: BUTTON_TYPE_LABELS[type],
      disabled: capped,
      'data-testid': `bloomreach-templates-form-buttons-type-option-${type.toLowerCase()}`,
    };
  });

  // FLOW/ORDER_DETAILS (and COPY_CODE on a non-Marketing template) are withheld from ctaTypes (see
  // getCtaTypes above) but edit-mode prefill can still hand this row one of them from an existing
  // template. With no option matching the row's own type, the compiled Select's renderValue (it
  // looks up `items` by value, independent of `disabled`) finds nothing and the box renders blank
  // instead of the type's label. Append it here, disabled, so it's found and displayed but stays
  // unpickable by any row — including this one switching away and back.
  if (!ctaTypes.includes(currentType)) {
    options.push({
      value: currentType,
      label: BUTTON_TYPE_LABELS[currentType],
      disabled: true,
      'data-testid': `bloomreach-templates-form-buttons-type-option-${currentType.toLowerCase()}`,
    });
  }

  return options;
}

// Enterprise rejects an RCI template with any other button, a header or a footer
// (ValidationUtils.validateRequestContactInfoTemplate) — the console replaces the whole buttons
// list with just this one whenever RCI is added, from the Add CTA menu (Buttons/index.tsx's
// onAddCTA, contactInfo branch) or by retyping an existing row (ButtonsCategory.tsx's
// handleCtaTypeChange, same "hasContactInfo -> updateButtons([...hasContactInfo])" short-circuit).
// Both call sites below share this pair so they can't drift apart.
function makeRciButton(buttonId) {
  const btn = createButton(BUTTON_TYPE.REQUEST_CONTACT_INFO);
  btn.text = field(REQUEST_CONTACT_INFO_BUTTON_TEXT);
  if (buttonId) btn.buttonId = buttonId;
  return btn;
}

function applyRciSideEffects(dispatch) {
  // RCI requires type TEXT with no header/footer (ValidationUtils.validateRequestContactInfoTemplate)
  // and no opt-out (which adds a quick reply Enterprise would reject the same way) — reset all
  // three instead of just hiding them, so nothing stale rides along if RCI is later removed and
  // the header/footer sections reappear.
  dispatch({ type: 'set', path: 'header.value', value: HEADER_TYPE.NONE });
  dispatch({ type: 'set', path: 'type.value', value: 'TEXT' });
  dispatch({ type: 'set', path: 'footer.footerText', value: field('') });
  dispatch({ type: 'set', path: 'optOutButton', value: false });
}

// Changing a button's TYPE must not let the old type's fields survive underneath the new one —
// e.g. URL -> PHONE_NUMBER must drop urlType/suffix/hasDeepLink/deepLink/fallbackLink, not just
// stop rendering them (payload.js's CTA_BUILDERS only reads the new type's own keys, but a
// leftover value would still be sitting in state, ready to resurface on a later change back).
// createButton (state.js) is the one place a button's shape is defined for a given type; mint a
// fresh one of the target type and carry over only what's genuinely shared across every type:
// buttonId (React key / error path identity) and text (the title already typed — the console
// preserves the same title across a type change). Every other field comes from the fresh button —
// verified against createButton's own BUTTON_TYPE_EXTRA_FIELDS factories above, not guessed.
//
// formReducer's 'updateButton' merges (`{ ...btn, ...action.value }`) rather than replacing, so a
// merge-only dispatch would leave any OLD field the new type doesn't have sitting untouched
// underneath. Blank every one of the old button's own keys to undefined first, then overlay the
// fresh button's real values on top; only buttonId/text get restored to the originals afterward.
function changeButtonType({
  dispatch, btn, nextType,
}) {
  if (nextType === btn.type) return;
  if (nextType === BUTTON_TYPE.REQUEST_CONTACT_INFO) {
    dispatch({ type: 'set', path: 'buttons', value: [makeRciButton(btn.buttonId)] });
    applyRciSideEffects(dispatch);
    return;
  }
  const fresh = createButton(nextType);
  const blanked = {};
  Object.keys(btn).forEach((key) => { blanked[key] = undefined; });
  dispatch({
    type: 'updateButton',
    buttonId: btn.buttonId,
    value: {
      ...blanked, ...fresh, buttonId: btn.buttonId, text: btn.text,
    },
  });
}

// One row: a title strip (drag handle + type label/select + remove) plus whatever fields that
// type needs. `errorIndex` is where `errors` currently attributes this button's mistakes to (see
// ButtonsSection's errorIndexFor) — not necessarily this row's live array position, which can
// drift out from under a stale error after a reorder.
function ButtonRow({
  btn, errorIndex, dispatch, errors, disabled, onRemove, countByType, ctaTypes,
  isDragOver, onDragStart, onDragEnd, onDragOver, onDrop, deepLinkOfferable,
}) {
  const path = `buttons.${errorIndex}`;
  const isQuickReply = btn.type === BUTTON_TYPE.QUICK_REPLY;
  const isCopyCode = btn.type === BUTTON_TYPE.COPY_CODE;
  const isRci = btn.type === BUTTON_TYPE.REQUEST_CONTACT_INFO;
  // LTO's own URL/copy-code CTAs (Category.js/Lto.js) — no type change, remove or drag, matching
  // the console's isCtaSelectDisabled/isRemoveButtonDisabled/disabledReorderButtonIds for LTO
  // (T6). RCI is intentionally NOT locked this way — the console still allows retyping an RCI row
  // away via its own Select (ButtonsCategory.tsx), only its title is fixed (below).
  const isLocked = Boolean(btn.locked);
  const rowClassName = isDragOver
    ? 'br-tpl-form-buttons-row br-tpl-form-buttons-row--drag-over'
    : 'br-tpl-form-buttons-row';

  return (
    <div
      className={rowClassName}
      data-testid={`bloomreach-templates-form-buttons-row-${btn.buttonId}`}
      onDragOver={(disabled || isLocked) ? undefined : onDragOver}
      onDrop={(disabled || isLocked) ? undefined : onDrop}
    >
      <div className="br-tpl-form-card-heading-row">
        <div className="br-tpl-form-card-heading">
          {/* draggable lives on this handle alone, never the row — a draggable ancestor turns off
              native text selection for every input nested inside it, which would break editing
              the row's own text fields. */}
          <span
            draggable={!disabled && !isLocked}
            onDragStart={(disabled || isLocked) ? undefined : onDragStart}
            onDragEnd={(disabled || isLocked) ? undefined : onDragEnd}
            data-testid={`bloomreach-templates-form-buttons-drag-handle-${btn.buttonId}`}
          >
            <Icon
              customPath={DRAG_HANDLE_ICON_URL}
              alt="Drag to reorder"
              size={18}
              showCursor={!disabled && !isLocked}
              iconStyles={{ opacity: (disabled || isLocked) ? 0.4 : 1 }}
            />
          </span>
          {isQuickReply ? (
            <Typography variant="body2" fontWeight={600}>{BUTTON_TYPE_LABELS[btn.type]}</Typography>
          ) : (
            // QUICK_REPLY never reaches this branch — quick replies are their own group with
            // their own add affordance, and only a CTA row's type can be changed in place.
            <Select
              label="Select type"
              items={buildTypeOptions(btn.type, countByType, ctaTypes)}
              value={btn.type}
              handleChange={(selected) => changeButtonType({
                dispatch, btn, nextType: Array.isArray(selected) ? selected[0] : selected,
              })}
              disabled={disabled || isLocked}
              width="200px"
              data-testid={`bloomreach-templates-form-buttons-type-${btn.buttonId}`}
            />
          )}
        </div>
        <Tooltip
          placement="right"
          title="Remove"
          data-testid={`bloomreach-templates-form-buttons-remove-tooltip-${btn.buttonId}`}
        >
          <span>
            {/* Console's own remove control is this library icon, not a trash can
                (C/Buttons/ButtonsCategory.tsx:614-624) — "x" exists in our own Icon too. */}
            <Icon
              name="x"
              size={18}
              showCursor={!disabled && !isLocked}
              iconStyles={{ opacity: (disabled || isLocked) ? 0.4 : 1 }}
              onClick={(disabled || isLocked) ? undefined : () => onRemove(btn.buttonId)}
              dataTestId={`bloomreach-templates-form-buttons-remove-${btn.buttonId}`}
            />
          </span>
        </Tooltip>
      </div>
      <div className="br-tpl-form-buttons-fields">
        <TextField
          fullWidth
          size="small"
          label={isQuickReply ? 'Quick Reply Title' : 'Button Title'}
          value={isRci ? REQUEST_CONTACT_INFO_BUTTON_TEXT : (isCopyCode ? COPY_CODE_BUTTON_LABEL : btn.text.value)}
          onChange={(event) => dispatch({
            type: 'updateButton',
            buttonId: btn.buttonId,
            value: { text: { value: event.target.value, error: '' } },
          })}
          disabled={disabled || isCopyCode || isRci}
          // validate.js requires `text` for every type except PASSKEY and COPY_CODE ("Every button
          // needs text except PASSKEY... COPY_CODE") — COPY_CODE's and REQUEST_CONTACT_INFO's
          // titles are both fixed (COPY_CODE_BUTTON_LABEL / REQUEST_CONTACT_INFO_BUTTON_TEXT above)
          // rather than editable, matching the console locking the same two fields. No per-type
          // "optional" hint otherwise, since one would contradict the actual submit-time error.
          placeholder={isQuickReply ? 'Type quick reply title' : 'Type button title'}
          autoComplete="off"
          inputProps={{
            maxLength: BUTTON_TEXT_MAX_CHARS,
            'data-testid': `bloomreach-templates-form-buttons-text-${btn.buttonId}`,
          }}
          {...errorProps(errors, `${path}.text.value`)}
        />
        {renderExtraFields({
          btn, index: errorIndex, dispatch, errors, disabled, deepLinkOfferable,
        })}
      </div>
    </div>
  );
}

// Pure: returns a NEW buttons array with the two rows at `fromId`/`toId` reordered relative to
// each other, without disturbing anything outside `groupRows`' own set of flat-array slots — the
// other button group (quick reply vs. CTA) keeps both its buttons AND their flat indices exactly
// as they were, since only the positions `groupRows` already occupied ever change contents. This
// is also what stops a cross-group drop: `groupRows` only ever lists ONE group's rows, so a
// buttonId from the other group is simply never found in it (findIndex -1) and the call is a
// silent no-op — returns `buttons` itself (same reference) so callers can skip dispatching.
function reorderWithinGroup(buttons, groupRows, fromId, toId) {
  const fromPos = groupRows.findIndex(({ btn }) => btn.buttonId === fromId);
  const toPos = groupRows.findIndex(({ btn }) => btn.buttonId === toId);
  if (fromPos === -1 || toPos === -1 || fromPos === toPos) return buttons;

  const reordered = groupRows.map(({ btn }) => btn);
  const [moved] = reordered.splice(fromPos, 1);
  reordered.splice(toPos, 0, moved);

  const next = buttons.slice();
  groupRows.forEach(({ index }, pos) => { next[index] = reordered[pos]; });
  return next;
}

// Buttons — Quick Reply plus one CTA per WhatsApp call-to-action type. Hidden for Carousel
// (carousel buttons live inside the carousel section, a separate task), Authentication
// (a fixed OTP/Passkey button with no user-editable list — AuthButtonsText.js, also separate), and
// Call Permission Request (Enterprise's CPR template carries no buttons at all — console mirror,
// Buttons/index.tsx:404).
const ButtonsSection = ({
  state, dispatch, errors, disabled, isMmLite,
}) => {
  const { buttons } = state;

  // validate.js keys every button error by its index at Submit time (buttons.${index}.field). A
  // drag reorder (or an add/remove) can change state.buttons' order without a fresh validateForm()
  // pass, so a naive `errors[buttons.${liveIndex}...]` lookup would then read a stale error under
  // whichever button happens to occupy that old slot now — a different button's mistake shown on
  // an innocent row. Remember the buttonId order `errors` was actually computed against, and look
  // each row's own errors up by where ITS id sat then (errorIndexFor below), not where it sits
  // now. Updated synchronously during render — React's own documented pattern for resetting state
  // when a prop changes — rather than in an effect, so a fresh validation pass is attributed
  // correctly on the very render that receives it, not one paint later (an effect-driven ref would
  // lag a full commit behind, and mutating a ref triggers no re-render to correct the display).
  const [prevErrors, setPrevErrors] = useState(errors);
  const [errorOrder, setErrorOrder] = useState(() => buttons.map((btn) => btn.buttonId));
  if (errors !== prevErrors) {
    setPrevErrors(errors);
    setErrorOrder(buttons.map((btn) => btn.buttonId));
  }

  // Drag-reorder state: which row's handle is currently being dragged, and which row it's
  // currently hovering over (for the drop-target affordance). Both buttonId-keyed so they stay
  // meaningful across the very state.buttons reorder they describe.
  const [draggedButtonId, setDraggedButtonId] = useState(null);
  const [dragOverButtonId, setDragOverButtonId] = useState(null);

  if (isCarousel(state) || isAuthentication(state) || isCallPermissionRequest(state)) {
    return null;
  }

  const ctaTypes = getCtaTypes(state);
  const rciActive = hasRequestContactInfo(state);
  // Both this section (guaranteed past Carousel/Authentication by the early return above) and
  // Footer.js must actually render for the opt-out toggle to make sense — Footer.js only hides for
  // isLto/isCarousel/(isAuthentication && !isPasskey)/hasRequestContactInfo, so with Carousel and
  // Authentication already ruled out here, the conditions left to match are isLto and RCI. Flow
  // deliberately keeps opt-out: it never loses its footer or its buttons the way Carousel/LTO do
  // (Category.js only clears those for CAROUSEL/LTO), so it renders the same as Custom message here.
  const optOutOfferable = isMarketing(state) && !isLto(state) && !rciActive;
  const optOutActive = optOutOfferable && state.optOutButton;
  // MM Lite + Marketing gate for offering "Enable deep link" on a NEW url button — see
  // renderExtraFields' own comment above for what this does and doesn't withhold.
  const deepLinkOfferable = isMarketing(state) && Boolean(isMmLite);

  const errorIndexFor = (buttonId) => {
    const at = errorOrder.indexOf(buttonId);
    return at === -1 ? buttons.findIndex((btn) => btn.buttonId === buttonId) : at;
  };

  // The opt-out quick reply is never a row in state.buttons (state.js) — payload.js appends it at
  // build time — so it has to be added back in here, or a template already at TOTAL_BUTTONS_MAX
  // real buttons would still let "Quick Reply"/"Add CTA" add one more than the wire will accept.
  // rciActive folds in Enterprise's own "RCI must be the template's only button" rule (T5) — once
  // present, both add affordances below go fully inert rather than merely capped.
  const atTotalCap = (buttons.length + (optOutActive ? 1 : 0)) >= TOTAL_BUTTONS_MAX || rciActive;
  const countByType = {};
  buttons.forEach((btn) => {
    countByType[btn.type] = (countByType[btn.type] || 0) + 1;
  });

  // Adding Request Contact Info replaces the whole list (T5) — same shape changeButtonType's own
  // RCI branch above builds, so the two can't drift apart.
  const addButton = (type) => {
    if (type === BUTTON_TYPE.REQUEST_CONTACT_INFO) {
      dispatch({ type: 'set', path: 'buttons', value: [makeRciButton()] });
      applyRciSideEffects(dispatch);
      return;
    }
    dispatch({ type: 'addButton', button: createButton(type) });
  };
  const removeButton = (buttonId) => dispatch({ type: 'removeButton', buttonId });

  const rows = buttons.map((btn, index) => ({ btn, index }));
  const quickReplyRows = rows.filter(({ btn }) => btn.type === BUTTON_TYPE.QUICK_REPLY);
  const ctaRows = rows.filter(({ btn }) => btn.type !== BUTTON_TYPE.QUICK_REPLY);

  // Drag handlers are built per-group (bound to that group's own row list) so a drop can only ever
  // reorder within the group it's handed — see reorderWithinGroup's own comment for why that's
  // enough to make a cross-group drop a silent no-op rather than something to separately branch on.
  const handleDragStart = (buttonId) => (event) => {
    event.dataTransfer.effectAllowed = 'move';
    // Firefox refuses to start a native drag unless dataTransfer carries data; the id itself is
    // read back from React state in the drop handler below, not from dataTransfer — dragover
    // handlers can't reliably read dataTransfer values mid-drag in every browser, only at drop.
    event.dataTransfer.setData('text/plain', buttonId);
    setDraggedButtonId(buttonId);
  };

  const handleDragEnd = () => {
    setDraggedButtonId(null);
    setDragOverButtonId(null);
  };

  // Per-group, like makeHandleDrop below — only preventDefault() (i.e. allow a drop) when the
  // hovered row is in the same group as the button being dragged. Skipping it for the other group
  // leaves the browser's default action in force, which is "reject drop": the cursor shows its
  // native no-drop glyph over there and onDrop never fires, on top of reorderWithinGroup's own
  // no-op safety net below — the cross-group block is visible, not just structurally enforced.
  const makeHandleDragOver = (groupRows) => (buttonId) => (event) => {
    if (!draggedButtonId || !groupRows.some(({ btn }) => btn.buttonId === draggedButtonId)) return;
    event.preventDefault();
    if (dragOverButtonId !== buttonId) setDragOverButtonId(buttonId);
  };

  const makeHandleDrop = (groupRows) => (buttonId) => (event) => {
    event.preventDefault();
    setDragOverButtonId(null);
    const sourceId = draggedButtonId;
    setDraggedButtonId(null);
    if (!sourceId) return;
    const next = reorderWithinGroup(buttons, groupRows, sourceId, buttonId);
    if (next !== buttons) dispatch({ type: 'set', path: 'buttons', value: next });
  };

  const menuItems = ctaTypes.map((type) => {
    const capped = isCtaTypeCapped(type, countByType);
    return {
      value: type,
      // Console puts the "limit reached" reason in a per-item tooltip. Checked whether this
      // library could host one: Dropdown.types.d.ts's Item has no tooltip/title field, and a
      // capped item renders as a disabled MUI MenuItem (pointerEvents: 'none', both in MUI's own
      // MenuItem and in this library's compiled Dropdown chunk) — a <Tooltip> stuffed into `label`
      // would sit behind that pointer-events block and need its own override just to receive
      // hover. That's the hack we're told not to add, so the label stays plain; isNonClickable
      // alone carries the "capped" signal.
      label: BUTTON_TYPE_LABELS[type],
      isNonClickable: capped,
      dataTestId: `bloomreach-templates-form-buttons-add-cta-option-${type.toLowerCase()}`,
    };
  });

  // Section-level cap errors validate.js can set: `buttons` (total > 10) and `buttons.{TYPE}` (one
  // CTA type's own cap exceeded) — distinct from the per-row `buttons.{index}.*` paths the fields
  // above read. Keyed off ctaTypes (not the fixed CTA_TYPE_MAX keys) so it only ever surfaces a cap
  // error for a type this render is actually offering — same behaviour as before COPY_CODE became
  // category-conditional.
  const capErrorKeys = ['buttons', ...ctaTypes.map((type) => `buttons.${type}`)];
  const capErrors = capErrorKeys
    .map((key) => ({ key, message: errors[key] }))
    .filter(({ message }) => Boolean(message));

  // Quick Reply / Call-to-action are built as two standalone JSX blocks (not inlined straight
  // into the return below) so their relative position can follow quickRepliesFirst(state) — the
  // group whose type sits first in state.buttons renders first, same rule payload.js's
  // computeQuickReplyFirst sends on the wire and Form/index.js's buildPreviewTemplate lists by
  // (state.js's quickRepliesFirst — see its own comment) — instead of always rendering Quick
  // Reply on top the way two literal, sequential JSX blocks would.
  const quickReplyGroup = (quickReplyRows.length > 0 || optOutActive) && (
    <div>
      <Typography variant="caption" className="br-tpl-form-helper-text">Quick Reply</Typography>
      <div className="br-tpl-form-variable-rows" data-testid="bloomreach-templates-form-buttons-quick-reply-group">
        {quickReplyRows.map(({ btn }) => (
          <ButtonRow
            key={btn.buttonId}
            btn={btn}
            errorIndex={errorIndexFor(btn.buttonId)}
            dispatch={dispatch}
            errors={errors}
            disabled={disabled}
            onRemove={removeButton}
            countByType={countByType}
            ctaTypes={ctaTypes}
            isDragOver={dragOverButtonId === btn.buttonId}
            onDragStart={handleDragStart(btn.buttonId)}
            onDragEnd={handleDragEnd}
            onDragOver={makeHandleDragOver(quickReplyRows)(btn.buttonId)}
            onDrop={makeHandleDrop(quickReplyRows)(btn.buttonId)}
            deepLinkOfferable={deepLinkOfferable}
          />
        ))}
        {/* Marketing opt-out's own quick reply — display-only, never a row in state.buttons
            (state.js). Always last, matching what Enterprise actually does to the LAST quick
            reply server-side (MessageTemplate.java:1679-1682, see state.js's own comment).
            Not a ButtonRow: it has no buttonId, and must not be editable, removable or
            draggable — so it renders its own small, hardcoded version of that row shape
            instead of being forced through a component built for a real button. Stays last
            within this group regardless of which group (this one or CTA's) renders first. */}
        {optOutActive && (
          <div
            className="br-tpl-form-buttons-row"
            data-testid="bloomreach-templates-form-buttons-optout-row"
          >
            <div className="br-tpl-form-card-heading-row">
              <div className="br-tpl-form-card-heading">
                <Icon customPath={DRAG_HANDLE_ICON_URL} alt="Drag to reorder" size={18} iconStyles={{ opacity: 0.4 }} />
                <Typography variant="body2" fontWeight={600}>Quick Reply</Typography>
              </div>
            </div>
            <div className="br-tpl-form-buttons-fields">
              <TextField
                fullWidth
                size="small"
                label="Quick Reply Title"
                value={MARKETING_OPT_OUT_BUTTON_TEXT}
                disabled
                autoComplete="off"
                inputProps={{ 'data-testid': 'bloomreach-templates-form-buttons-optout-text' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const ctaGroup = ctaRows.length > 0 && (
    <div>
      <Typography variant="caption" className="br-tpl-form-helper-text">Call to action</Typography>
      <div className="br-tpl-form-variable-rows" data-testid="bloomreach-templates-form-buttons-cta-group">
        {ctaRows.map(({ btn }) => (
          <ButtonRow
            key={btn.buttonId}
            btn={btn}
            errorIndex={errorIndexFor(btn.buttonId)}
            dispatch={dispatch}
            errors={errors}
            disabled={disabled}
            onRemove={removeButton}
            countByType={countByType}
            ctaTypes={ctaTypes}
            isDragOver={dragOverButtonId === btn.buttonId}
            onDragStart={handleDragStart(btn.buttonId)}
            onDragEnd={handleDragEnd}
            onDragOver={makeHandleDragOver(ctaRows)(btn.buttonId)}
            onDrop={makeHandleDrop(ctaRows)(btn.buttonId)}
            deepLinkOfferable={deepLinkOfferable}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="br-tpl-form-card" data-testid="bloomreach-templates-form-buttons">
      <div className="br-tpl-form-card-heading-row">
        <div className="br-tpl-form-card-heading">
          <Typography variant="subtitle1" data-testid="bloomreach-templates-form-buttons-heading">
            Buttons
          </Typography>
          <span className="br-tpl-form-optional-pill" data-testid="bloomreach-templates-form-buttons-optional">
            Optional
          </span>
        </div>
      </div>
      <div className="br-tpl-form-inline-row">
        <Typography variant="caption" className="br-tpl-form-helper-text">
          Please choose buttons to be added to the template. You can choose upto 10 buttons
        </Typography>
        {/* Console's max-button-limits tooltip (C/Buttons/index.tsx:449-478) — copy-exact,
            including the " / Call to WhatsApp" clause: we offer voice call to every account
            (see BASE_CTA_TYPES' own comment above), so that clause is never conditional here. */}
        <Tooltip
          placement="right"
          title={(
            <span data-testid="bloomreach-templates-form-buttons-max-limits-tooltip-text">
              Maximum button limits
              <br />
              {isMarketing(state) && (
                <>
                  Copy offer code - 1
                  <br />
                </>
              )}
              URL - 2
              <br />
              Phone Number / Call to WhatsApp - 1
              <br />
            </span>
          )}
          data-testid="bloomreach-templates-form-buttons-max-limits-tooltip"
        >
          <span>
            <Icon customPath={MAX_BUTTON_LIMITS_INFO_ICON_URL} alt="Button Info" size={14} showCursor />
          </span>
        </Tooltip>
      </div>

      {capErrors.map(({ key, message }) => {
        const type = key === 'buttons' ? null : key.slice('buttons.'.length);
        const label = type ? BUTTON_TYPE_LABELS[type] : null;
        return (
          <Typography
            key={key}
            variant="caption"
            color="error"
            data-testid={`bloomreach-templates-form-buttons-cap-error-${type ? type.toLowerCase() : 'total'}`}
          >
            {label ? `${label}: ${friendlyError(message)}` : friendlyError(message)}
          </Typography>
        );
      })}

      <div className="br-tpl-form-buttons-actions">
        <Button
          size="small"
          variant="outlined"
          startIcon="plus"
          disabled={disabled || atTotalCap}
          onClick={() => addButton(BUTTON_TYPE.QUICK_REPLY)}
          data-testid="bloomreach-templates-form-buttons-add-quick-reply"
        >
          Quick Reply
        </Button>

        {/* Dropdown (not Select) — an action menu, not a persistent chosen value: menuItems
            fire addButton on click and the control resets, it never "shows" a current pick.
            `value` is required by DropdownProps but unused for display in customIcon mode (only
            the customIcon node renders as the trigger) — confirmed by reading the compiled
            component, not guessed. */}
        <Dropdown
          size="small"
          value=""
          menuControlOption="customIcon"
          disabled={disabled || atTotalCap}
          handleChange={(selectedValue) => addButton(selectedValue)}
          menuItems={menuItems}
          dataTestId="bloomreach-templates-form-buttons-add-cta"
          customIcon={(
            <Button
              size="small"
              variant="outlined"
              startIcon="plus"
              disabled={disabled || atTotalCap}
              component="span"
              data-testid="bloomreach-templates-form-buttons-add-cta-trigger"
            >
              Add CTA
            </Button>
          )}
        />
      </div>

      {optOutOfferable && (
        <div>
          <div className="br-tpl-form-inline-row">
            <Checkbox
              checked={state.optOutButton}
              onChange={(event) => dispatch({
                type: 'set', path: 'optOutButton', value: event.target.checked,
              })}
              label="Add marketing opt-out button"
              disabled={disabled}
              data-testid="bloomreach-templates-form-buttons-optout-checkbox"
            />
            <Tooltip
              placement="right"
              title={`Adds a "${MARKETING_OPT_OUT_BUTTON_TEXT}" quick reply and replaces the footer, so customers can opt out of marketing messages.`}
              data-testid="bloomreach-templates-form-buttons-optout-tooltip"
            >
              <span>
                <Icon name="question" size={18} showCursor />
              </span>
            </Tooltip>
          </div>
          {errors['buttons.optOutButton'] && (
            <Typography variant="caption" color="error" data-testid="bloomreach-templates-form-buttons-optout-error">
              {friendlyError(errors['buttons.optOutButton'])}
            </Typography>
          )}
        </div>
      )}

      {quickRepliesFirst(state) ? (
        <>
          {quickReplyGroup}
          {ctaGroup}
        </>
      ) : (
        <>
          {ctaGroup}
          {quickReplyGroup}
        </>
      )}
    </div>
  );
};

export default ButtonsSection;
